<script setup lang="ts">
import { eventMapLabelTransform, type EventMapLabelLayout } from '~/shared-kernel/presentation/event-map/event-map-label-layout'
import EventMapIndividualSaleIcon from '~/shared-kernel/presentation/event-map/EventMapIndividualSaleIcon.vue'
import { Application, Container, Graphics, Rectangle, Text } from 'pixi.js'
import { nextTick, onBeforeUnmount, onMounted, shallowRef, watch } from 'vue'
import {
  eventMapDocumentBounds,
  eventMapFitTransform,
  eventMapLocalityBounds,
  eventMapUnitHitArea,
  eventMapUnitPosition
} from '~/shared-kernel/presentation/event-map/event-map.geometry'
import {
  changedEventMapDocumentUnits,
  changedEventMapUnitVisualIds,
  createEventMapUnitVisualState,
  resolveEventMapUnitRenderState,
  type EventMapUnitVisualState
} from '~/shared-kernel/presentation/event-map/event-map-unit-visual-state'
import { preferredCanvasRendererResolution, sharpCanvasTextResolution } from '~/shared-kernel/presentation/canvas/canvas-text-resolution'
import {
  EVENT_MAP_DEFAULT_MIN_ZOOM as DEFAULT_MIN_ZOOM,
  eventMapPinchGesture,
  eventMapPinchViewport,
  eventMapZoomAround,
  type EventMapPinchGesture
} from '~/shared-kernel/presentation/event-map/event-map-viewport-gesture'
import type {
  EventMapBounds,
  EventMapDocument,
  EventMapSectionView,
  EventMapUnitView,
  EventMapViewportTransform
} from '~/shared-kernel/presentation/event-map/event-map.types'

const props = withDefaults(defineProps<{
  document: EventMapDocument
  focusedLocalityId: string | null
  inspectedUnitId: string | null
  ownedUnitIds?: string[] | undefined
  pendingUnitIds?: string[] | undefined
  ownedLocalityIds?: string[] | undefined
  pendingLocalityIds?: string[] | undefined
  selectable?: boolean
  activatable?: boolean
  viewportCommand: { id: number; kind: 'fit' | 'zoom-in' | 'zoom-out' }
}>(), { activatable: false })

const emit = defineEmits<{
  focusLocality: [localityId: string]
  inspectLocality: [localityId: string | null]
  inspectUnit: [unitId: string | null]
  selectUnit: [unitId: string]
  activateUnit: [unitId: string]
  selectLocality: [localityId: string]
  hoverPosition: [position: { x: number; y: number; halfWidth: number; halfHeight: number } | null]
  error: []
}>()

const host = useTemplateRef<HTMLDivElement>('host')
let app: Application | null = null
let viewportLayer: Container | null = null
let staticLayer: Container | null = null
let interactionLayer: Container | null = null
let resizeObserver: ResizeObserver | null = null
let gestureTarget: HTMLCanvasElement | null = null
let disposed = false
let renderFrame: number | null = null
let mapFontAvailable = false
let mapFontLoad: Promise<void> | null = null
let viewport: EventMapViewportTransform = { zoom: 1, panX: 0, panY: 0 }
let pan: { x: number; y: number; panX: number; panY: number } | null = null
// Set once a drag passes the slop, so releasing over a seat/locality pans instead of selecting it.
let panMoved = false
let pinch: EventMapPinchGesture | null = null
let suppressTouchTap = false
let minZoom = DEFAULT_MIN_ZOOM
let pinnedUnitId: string | null = null
let unitVisualState: EventMapUnitVisualState = createEventMapUnitVisualState({
  inspectedUnitId: props.inspectedUnitId,
  ownedUnitIds: props.ownedUnitIds ?? [],
  pendingUnitIds: props.pendingUnitIds ?? []
})
let ownedLocalitySnapshot = new Set(props.ownedLocalityIds)
let pendingLocalitySnapshot = new Set(props.pendingLocalityIds)
const unitGraphics = new Map<string, {
  graphic: Graphics
  label: Text
  unit: EventMapUnitView
  defaultTextColor: number
}>()

function requestRender() {
  if (!app || disposed || renderFrame !== null) return
  renderFrame = requestAnimationFrame(() => {
    renderFrame = null
    if (!disposed) app?.render()
  })
}

function clear(layer: Container | null) {
  layer?.removeChildren().forEach(child => {
    // Map Graphics own their contexts; Text textures remain managed by Pixi's reference counting.
    if (child instanceof Graphics) child.destroy({ children: true, context: true })
    else child.destroy({ children: true })
  })
}

function refreshMapFont() {
  if (disposed || mapFontAvailable || mapFontLoad) return
  // check() also succeeds when no matching FontFace is registered yet. Only
  // resolved, loaded faces are safe to measure and cache under the Montserrat style.
  mapFontLoad = document.fonts.load('700 13px Montserrat').then(faces => {
    if (disposed || !faces.length || faces.some(face => face.status !== 'loaded')) return
    mapFontAvailable = true
    document.fonts.removeEventListener('loadingdone', refreshMapFont)
    // Recreate measurements, placement and textures together, without changing the viewport.
    if (app) redraw()
  }).catch(() => {
    // A failed optional font must not block interaction or replace the usable fallback.
  }).finally(() => { mapFontLoad = null })
}

function colorValue(color: string | undefined, fallback: number) {
  if (!color || !/^#[\da-f]{6}$/i.test(color)) return fallback
  return Number.parseInt(color.slice(1), 16)
}

function drawBounds(graphic: Graphics, element: EventMapBounds & { rotation: number }, shape: 'square' | 'circle' | 'polygon', points?: Array<{ x: number; y: number }>) {
  if (shape === 'polygon' && points?.length) graphic.poly(points.flatMap(point => [point.x, point.y]))
  else if (shape === 'circle') graphic.ellipse(element.x + element.width / 2, element.y + element.height / 2, element.width / 2, element.height / 2)
  else graphic.rect(element.x, element.y, element.width, element.height)

  if (element.rotation) {
    const centerX = element.x + element.width / 2
    const centerY = element.y + element.height / 2
    graphic.pivot.set(centerX, centerY)
    graphic.position.set(centerX, centerY)
    graphic.rotation = element.rotation * Math.PI / 180
  }
}

function label(value: string, x: number, y: number, size = 13, color = 0x172033) {
  const text = new Text({
    text: value,
    resolution: sharpCanvasTextResolution(app?.renderer.resolution, viewport.zoom),
    style: { fill: color, fontFamily: mapFontAvailable ? 'Montserrat, sans-serif' : 'sans-serif', fontSize: size, fontWeight: '700' }
  })
  text.anchor.set(0.5)
  text.position.set(x, y)
  text.eventMode = 'none'
  return text
}

function placeObjectLabel(text: Text, object: { x: number; y: number; width: number; height: number; rotation: number; labelLayout?: EventMapLabelLayout }) {
  const placement = eventMapLabelTransform(object, text, { x: text.x, y: text.y })
  text.position.set(placement.x, placement.y)
  text.rotation = placement.rotation
  text.scale.set(placement.scale)
  return text
}

function seatNumberLabel(value: string, x: number, y: number, markerSize: number, color: number) {
  const size = Math.max(9, Math.min(13, markerSize * 0.55))
  return label(value, x, y, size, color)
}

function sectionCenter(section: EventMapSectionView) {
  return { x: section.x + section.width / 2, y: section.y + section.height / 2 }
}

function drawStaticContext() {
  if (!staticLayer) return
  clear(staticLayer)
  const focused = props.focusedLocalityId

  for (const element of props.document.globalElements) {
    const graphic = new Graphics()
    drawBounds(graphic, element, element.kind === 'circle' ? 'circle' : 'square')
    graphic.fill({ color: element.kind === 'stage' ? 0x172033 : 0xf8fafc, alpha: focused ? 0.35 : 0.92 })
    graphic.stroke({ width: 2, color: 0x64748b, alpha: focused ? 0.35 : 0.8 })
    staticLayer.addChild(graphic)
    if (!focused && element.label) staticLayer.addChild(placeObjectLabel(label(element.label, element.x + element.width / 2, element.y + element.height / 2, 12, element.kind === 'stage' ? 0xffffff : 0x172033), element))
  }

  for (const section of props.document.sections) {
    const locality = props.document.localities.find(item => item.id === section.localityId)
    const active = !focused || focused === section.localityId
    const owned = Boolean(locality && props.ownedLocalityIds?.includes(locality.id))
    const pending = Boolean(locality && props.pendingLocalityIds?.includes(locality.id))
    const unavailable = locality?.available === false
    const graphic = new Graphics()
    drawBounds(graphic, section, section.shape, section.points)
    // Match the floor-plan designer's own rendering (FloorPlanCanvas.client.vue): solid, un-blended
    // fill/stroke colors so the purchase map looks exactly like the configured design, not a faded tint of it.
    graphic.fill({
      color: owned || pending
        ? SELECTED_FILL_COLOR
        : unavailable
          ? 0x94a3b8
          : colorValue(section.appearance?.fillColor ?? section.zone?.color, 0x0f766e),
      alpha: active ? 1 : 0.15
    })
    graphic.stroke({
      width: owned || pending ? Math.max(section.appearance?.strokeWidth ?? 3, 4) : active ? (section.appearance?.strokeWidth ?? 3) : 1,
      color: owned || pending ? SELECTED_STROKE_COLOR : active ? colorValue(section.appearance?.strokeColor, 0x0f766e) : 0x94a3b8,
      alpha: active ? 1 : 0.35
    })
    staticLayer.addChild(graphic)
    if (!focused || locality?.selectableAsLocality) {
      const center = sectionCenter(section)
      const textColor = owned || pending ? SELECTED_TEXT_COLOR : colorValue(section.appearance?.textColor, 0x172033)
      const name = placeObjectLabel(label(locality?.name ?? section.zone?.name ?? '', center.x, center.y, section.labelFontSize, textColor), section)
      staticLayer.addChild(name)
    }
  }
}

// A saturated confirmation color makes a held unit unmistakable without
// resembling the previous navy state or competing with unavailable markers.
const SELECTED_FILL_COLOR = 0x0f766e
const SELECTED_STROKE_COLOR = 0x5eead4
const SELECTED_TEXT_COLOR = 0xffffff
// tq-ui-exception: fixed canvas warning colors identify inventory blocks; only staff Blocks supplies this flag.
const BLOCKED_FILL_COLOR = 0xf59e0b
const BLOCKED_STROKE_COLOR = 0x92400e
const BLOCKED_TEXT_COLOR = 0x172033
// Finger jitter on a tap stays under this; beyond it the gesture is a pan.
const PAN_SLOP_PX = 8

function paintUnit(graphic: Graphics, unit: EventMapUnitView) {
  const renderState = resolveEventMapUnitRenderState(unit.id, unit.available, unitVisualState)
  const { blocked, owned, outlineColor } = renderState
  graphic.clear()
  requestRender()
  graphic.cursor = props.activatable
    ? 'pointer'
    : props.selectable
      ? (renderState.canSelect ? 'pointer' : 'not-allowed')
      : 'default'

  if (unit.kind === 'seat') {
    const size = unit.markerStyle?.size ?? 20
    if (unit.markerStyle?.shape === 'square') graphic.rect(-size / 2, -size / 2, size, size)
    else graphic.circle(0, 0, size / 2)
    graphic
      .fill({ color: owned ? SELECTED_FILL_COLOR : unit.blocked ? BLOCKED_FILL_COLOR : (blocked ? 0x94a3b8 : colorValue(unit.markerStyle?.fillColor, 0x0f766e)) })
      .stroke({
        width: owned ? Math.max(unit.markerStyle?.strokeWidth ?? 2, 3) : (unit.markerStyle?.strokeWidth ?? 2),
        color: owned ? SELECTED_STROKE_COLOR : unit.blocked ? BLOCKED_STROKE_COLOR : (blocked ? 0x475569 : colorValue(unit.markerStyle?.strokeColor, 0xffffff))
      })
    if (outlineColor !== null) {
      const outlineSize = size + 8
      if (unit.markerStyle?.shape === 'square') graphic.rect(-outlineSize / 2, -outlineSize / 2, outlineSize, outlineSize)
      else graphic.circle(0, 0, outlineSize / 2)
      graphic.stroke({ width: 3, color: outlineColor, alpha: 0.95 })
    }
  } else {
    graphic.roundRect(-unit.width / 2, -unit.height / 2, unit.width, unit.height, unit.groupKind === 'table' ? unit.width / 2 : 7)
      .fill({ color: owned ? SELECTED_FILL_COLOR : unit.blocked ? BLOCKED_FILL_COLOR : (blocked ? 0x94a3b8 : colorValue(unit.markerStyle?.fillColor, 0xdbeafe)) })
      .stroke({
        width: owned ? Math.max(unit.markerStyle?.strokeWidth ?? 2, 3) : (unit.markerStyle?.strokeWidth ?? 2),
        color: owned ? SELECTED_STROKE_COLOR : unit.blocked ? BLOCKED_STROKE_COLOR : (blocked ? 0x475569 : colorValue(unit.markerStyle?.strokeColor, 0x334155))
      })
    if (outlineColor !== null) {
      graphic
        .roundRect(-unit.width / 2 - 4, -unit.height / 2 - 4, unit.width + 8, unit.height + 8, unit.groupKind === 'table' ? (unit.width + 8) / 2 : 11)
        .stroke({ width: 3, color: outlineColor, alpha: 0.95 })
    }
  }
}

function updateUnitVisuals(unitIds: Iterable<string> = unitGraphics.keys()) {
  for (const unitId of unitIds) {
    const entry = unitGraphics.get(unitId)
    if (!entry) continue
    paintUnit(entry.graphic, entry.unit)
    entry.label.style.fill = unitVisualState.ownedUnitIds.has(unitId)
      ? SELECTED_TEXT_COLOR
      : entry.unit.blocked ? BLOCKED_TEXT_COLOR : entry.defaultTextColor
  }
}

function replaceUnitVisualState(next: EventMapUnitVisualState) {
  const changedIds = changedEventMapUnitVisualIds(unitVisualState, next)
  unitVisualState = next
  updateUnitVisuals(changedIds)
}

function updateUnitVisualState(update: Partial<{
  hoveredUnitId: string | null
  inspectedUnitId: string | null
  ownedUnitIds: readonly string[]
  pendingUnitIds: readonly string[]
}>) {
  replaceUnitVisualState({
    hoveredUnitId: update.hoveredUnitId === undefined ? unitVisualState.hoveredUnitId : update.hoveredUnitId,
    inspectedUnitId: update.inspectedUnitId === undefined ? unitVisualState.inspectedUnitId : update.inspectedUnitId,
    ownedUnitIds: update.ownedUnitIds === undefined ? unitVisualState.ownedUnitIds : new Set(update.ownedUnitIds),
    pendingUnitIds: update.pendingUnitIds === undefined ? unitVisualState.pendingUnitIds : new Set(update.pendingUnitIds)
  })
}

function updateUnitHitArea(graphic: Graphics, unit: EventMapUnitView) {
  const hit = eventMapUnitHitArea({ ...unit, x: 0, y: 0 }, viewport.zoom)
  if (graphic.hitArea instanceof Rectangle) Object.assign(graphic.hitArea, hit)
  else graphic.hitArea = new Rectangle(hit.x, hit.y, hit.width, hit.height)
}

function unitScreenPosition(graphic: Graphics, unit: EventMapUnitView) {
  const global = graphic.getGlobalPosition()
  const halfWidth = unit.kind === 'seat' ? (unit.markerStyle?.size ?? 20) / 2 : unit.width / 2
  const halfHeight = unit.kind === 'seat' ? (unit.markerStyle?.size ?? 20) / 2 : unit.height / 2
  return { x: global.x, y: global.y, halfWidth: halfWidth * viewport.zoom, halfHeight: halfHeight * viewport.zoom }
}

function bindUnitInteraction(graphic: Graphics, unit: EventMapUnitView) {
  graphic.eventMode = 'static'
  updateUnitHitArea(graphic, unit)
  const screenPosition = () => unitScreenPosition(graphic, unitGraphics.get(unit.id)?.unit ?? unit)
  graphic.on('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return
    updateUnitVisualState({ hoveredUnitId: unit.id })
    if (!pinnedUnitId) {
      emit('inspectUnit', unit.id)
      emit('hoverPosition', screenPosition())
    }
  })
  graphic.on('pointerout', (event) => {
    if (event.pointerType !== 'mouse') return
    if (unitVisualState.hoveredUnitId === unit.id) updateUnitVisualState({ hoveredUnitId: null })
    if (!pinnedUnitId) {
      emit('inspectUnit', null)
      emit('hoverPosition', null)
    }
  })
  graphic.on('pointertap', (event) => {
    event.stopPropagation()
    if (panMoved || (event.pointerType === 'touch' && suppressTouchTap)) return
    if (props.activatable) emit('activateUnit', unit.id)
    pinnedUnitId = pinnedUnitId === unit.id ? null : unit.id
    updateUnitVisualState({ inspectedUnitId: pinnedUnitId })
    emit('inspectUnit', pinnedUnitId)
    emit('hoverPosition', pinnedUnitId ? screenPosition() : null)
    const currentUnit = unitGraphics.get(unit.id)?.unit ?? unit
    if (
      props.selectable
      && resolveEventMapUnitRenderState(currentUnit.id, currentUnit.available, unitVisualState).canSelect
    ) emit('selectUnit', unit.id)
  })
}

function addSectionInteraction(section: EventMapSectionView, localityId: string) {
  if (!interactionLayer) return
  const locality = props.document.localities.find(item => item.id === localityId)
  const hit = new Graphics()
  drawBounds(hit, section, section.shape, section.points)
  hit.fill({ color: 0xffffff, alpha: 0.001 })
  hit.eventMode = 'static'
  hit.cursor = props.selectable
    ? (locality?.available === false ? 'not-allowed' : 'pointer')
    : 'default'
  hit.on('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return
    emit('inspectLocality', localityId)
    emit('hoverPosition', { x: event.global.x, y: event.global.y, halfWidth: 0, halfHeight: 0 })
  })
  hit.on('pointermove', (event) => {
    if (event.pointerType !== 'mouse') return
    emit('hoverPosition', { x: event.global.x, y: event.global.y, halfWidth: 0, halfHeight: 0 })
  })
  hit.on('pointerout', () => {
    emit('inspectLocality', null)
    emit('hoverPosition', null)
  })
  hit.on('pointertap', (event) => {
    event.stopPropagation()
    if (panMoved || (event.pointerType === 'touch' && suppressTouchTap)) return
    emit('focusLocality', localityId)
    if (
      props.selectable &&
      locality?.selectableAsLocality &&
      locality.available !== false
    ) {
      emit('selectLocality', localityId)
    }
  })
  interactionLayer.addChild(hit)
}

function drawInteractiveLayer() {
  if (!interactionLayer) return
  clear(interactionLayer)
  emit('inspectLocality', null)
  unitGraphics.clear()
  unitVisualState = createEventMapUnitVisualState({
    inspectedUnitId: props.inspectedUnitId,
    ownedUnitIds: props.ownedUnitIds ?? [],
    pendingUnitIds: props.pendingUnitIds ?? []
  })

  if (!props.focusedLocalityId) {
    for (const section of props.document.sections) {
      const localityId = section.localityId
      if (!localityId) continue
      addSectionInteraction(section, localityId)
    }
    return
  }

  for (const section of props.document.sections.filter(item => item.localityId === props.focusedLocalityId)) {
    const locality = props.document.localities.find(item => item.id === section.localityId)
    if (locality?.selectableAsLocality && section.localityId) {
      addSectionInteraction(section, section.localityId)
      continue
    }
    const layout = props.document.seatLayouts[section.id]
    if (!layout) continue
    for (const unit of layout.units) {
      const position = eventMapUnitPosition(section, unit)
       const graphic = new Graphics()
       graphic.position.set(position.x, position.y)
       paintUnit(graphic, unit)
       if (unit.kind === 'group') graphic.rotation = section.rotation * Math.PI / 180
       bindUnitInteraction(graphic, unit)
       interactionLayer.addChild(graphic)
      const defaultTextColor = colorValue(section.appearance?.textColor, 0x172033)
      const unitLabel = unit.kind === 'group'
        ? label(unit.label, position.x, position.y, 13, defaultTextColor)
        : seatNumberLabel(unit.number, position.x, position.y, unit.markerStyle?.size ?? 20, defaultTextColor)
      if (unitVisualState.ownedUnitIds.has(unit.id)) unitLabel.style.fill = SELECTED_TEXT_COLOR
      else if (unit.blocked) unitLabel.style.fill = BLOCKED_TEXT_COLOR
      unitGraphics.set(unit.id, { graphic, label: unitLabel, unit, defaultTextColor })
      interactionLayer.addChild(unitLabel)
    }
  }
}

const individualSaleMarkers = shallowRef<Array<{ id: string; x: number; y: number; expanded: boolean }>>([])

function applyViewport() {
  individualSaleMarkers.value = props.document.sections
    .filter(section => section.localityId === props.focusedLocalityId)
    .flatMap(section => (props.document.seatLayouts[section.id]?.units ?? [])
      .filter(unit => unit.individualSale)
      .map(unit => {
        const point = eventMapUnitPosition(section, unit)
        const halfSize = unit.kind === 'seat' ? (unit.markerStyle?.size ?? 20) / 2 : unit.width / 2
        const halfHeight = unit.kind === 'seat' ? halfSize : unit.height / 2
        return { id: unit.id, expanded: unit.expandedFromGroup ?? false, x: (point.x + halfSize) * viewport.zoom + viewport.panX, y: (point.y - halfHeight) * viewport.zoom + viewport.panY }
      }))
  viewportLayer?.scale.set(viewport.zoom)
  viewportLayer?.position.set(viewport.panX, viewport.panY)
  const inspected = pinnedUnitId ?? unitVisualState.hoveredUnitId
  const entry = inspected ? unitGraphics.get(inspected) : undefined
  if (entry) emit('hoverPosition', unitScreenPosition(entry.graphic, entry.unit))
  requestRender()
}

function updateViewport() {
  for (const { graphic, unit } of unitGraphics.values()) updateUnitHitArea(graphic, unit)
  const resolution = sharpCanvasTextResolution(app?.renderer.resolution, viewport.zoom)
  for (const layer of [staticLayer, interactionLayer]) {
    for (const child of layer?.children ?? []) {
      if (child instanceof Text && child.resolution !== resolution) child.resolution = resolution
    }
  }
  applyViewport()
}

function redraw() {
  drawStaticContext()
  drawInteractiveLayer()
  applyViewport()
}
function updateDocument(next: EventMapDocument, previous: EventMapDocument) {
  if (next === previous) {
    redraw()
    return
  }
  const changed = changedEventMapDocumentUnits(previous, next)
  if (changed === null) {
    redraw()
    return
  }
  for (const [id, unit] of changed) {
    const entry = unitGraphics.get(id)
    if (!entry) continue
    entry.unit = unit
    paintUnit(entry.graphic, unit)
    entry.label.style.fill = unitVisualState.ownedUnitIds.has(id)
      ? SELECTED_TEXT_COLOR
      : unit.blocked ? BLOCKED_TEXT_COLOR : entry.defaultTextColor
  }
}


function activeBounds() {
  return props.focusedLocalityId
    ? eventMapLocalityBounds(props.document, props.focusedLocalityId)
    : eventMapDocumentBounds(props.document)
}

function fit() {
  const bounds = activeBounds()
  if (!bounds || !app) return
  // Large maps on phones need less than the default floor to be seen whole; that fit becomes the zoom-out floor.
  viewport = eventMapFitTransform(bounds, { width: app.screen.width, height: app.screen.height }, {
    padding: Math.min(48, app.screen.width * 0.05),
    minZoom: 0.01
  })
  minZoom = Math.min(DEFAULT_MIN_ZOOM, viewport.zoom)
  redraw()
}

function zoom(delta: number, anchor?: { x: number; y: number }) {
  if (!app) return
  const zoomAnchor = anchor ?? { x: app.screen.width / 2, y: app.screen.height / 2 }
  viewport = eventMapZoomAround(viewport, viewport.zoom + delta, zoomAnchor, minZoom)
  updateViewport()
}

function localGesturePoint(clientX: number, clientY: number) {
  const bounds = gestureTarget?.getBoundingClientRect()
  return { x: clientX - (bounds?.left ?? 0), y: clientY - (bounds?.top ?? 0) }
}

function handleWheel(event: WheelEvent) {
  event.preventDefault()
  zoom(event.deltaY > 0 ? -0.1 : 0.1, localGesturePoint(event.clientX, event.clientY))
}

function touchPinchGesture(event: TouchEvent) {
  const first = event.touches.item(0)
  const second = event.touches.item(1)
  if (!first || !second) return null
  return eventMapPinchGesture(
    localGesturePoint(first.clientX, first.clientY),
    localGesturePoint(second.clientX, second.clientY)
  )
}

function handleTouchStart(event: TouchEvent) {
  // Keep suppression through every pointerup; only a new one-finger start begins a tappable gesture.
  suppressTouchTap = event.touches.length > 1
  if (event.touches.length !== 2) return
  event.preventDefault()
  pan = null
  pinch = touchPinchGesture(event)
}

function handleTouchMove(event: TouchEvent) {
  if (event.touches.length !== 2) return
  event.preventDefault()
  const current = touchPinchGesture(event)
  if (!current) return
  suppressTouchTap = true
  if (pinch) {
    viewport = eventMapPinchViewport(viewport, pinch, current, minZoom)
    updateViewport()
  }
  pinch = current
}

function handleTouchEnd(event: TouchEvent) {
  if (event.touches.length < 2) pinch = null
}

function bindViewportGestures(target: HTMLCanvasElement) {
  gestureTarget = target
  target.style.touchAction = 'none'
  target.addEventListener('wheel', handleWheel, { passive: false })
  target.addEventListener('touchstart', handleTouchStart, { passive: false })
  target.addEventListener('touchmove', handleTouchMove, { passive: false })
  target.addEventListener('touchend', handleTouchEnd)
  target.addEventListener('touchcancel', handleTouchEnd)
  target.addEventListener('webglcontextrestored', requestRender)
}

function unbindViewportGestures() {
  if (!gestureTarget) return
  gestureTarget.removeEventListener('wheel', handleWheel)
  gestureTarget.removeEventListener('touchstart', handleTouchStart)
  gestureTarget.removeEventListener('touchmove', handleTouchMove)
  gestureTarget.removeEventListener('touchend', handleTouchEnd)
  gestureTarget.removeEventListener('touchcancel', handleTouchEnd)
  gestureTarget.removeEventListener('webglcontextrestored', requestRender)
  gestureTarget = null
  pinch = null
  suppressTouchTap = false
}

function handleViewportCommand() {
  if (props.viewportCommand.kind === 'fit') fit()
  else zoom(props.viewportCommand.kind === 'zoom-in' ? 0.2 : -0.2)
}

async function mountPixi() {
  if (!host.value) return
  try {
    const application = new Application()
    await application.init({
      antialias: true,
      autoDensity: true,
      backgroundAlpha: 0,
      autoStart: false,
      sharedTicker: false,
      resizeTo: host.value,
      resolution: preferredCanvasRendererResolution(window.devicePixelRatio)
    })
    if (disposed || !host.value) {
      application.destroy({ removeView: true }, { children: true })
      return
    }
    app = application
    host.value.appendChild(app.canvas)
    bindViewportGestures(app.canvas)
    viewportLayer = new Container()
    staticLayer = new Container()
    interactionLayer = new Container()
    viewportLayer.addChild(staticLayer, interactionLayer)
    app.stage.addChild(viewportLayer)
    app.stage.eventMode = 'static'
    app.stage.hitArea = new Rectangle(0, 0, app.screen.width, app.screen.height)
    app.stage.on('pointerdown', (event) => {
      // Pan from anywhere: seats and localities cover most of a dense map.
      pan = { x: event.global.x, y: event.global.y, panX: viewport.panX, panY: viewport.panY }
      panMoved = false
      if (event.target !== app?.stage) return
      pinnedUnitId = null
      updateUnitVisualState({ inspectedUnitId: null })
      emit('inspectUnit', null)
      emit('inspectLocality', null)
      emit('hoverPosition', null)
    })
    app.stage.on('pointermove', (event) => {
      if (!pan) return
      const dx = event.global.x - pan.x
      const dy = event.global.y - pan.y
      if (!panMoved && Math.hypot(dx, dy) < PAN_SLOP_PX) return
      panMoved = true
      viewport = { ...viewport, panX: pan.panX + dx, panY: pan.panY + dy }
      applyViewport()
    })
    app.stage.on('pointerup', () => { pan = null })
    app.stage.on('pointerupoutside', () => { pan = null })
    fit()
  } catch {
    if (!disposed) emit('error')
  }
}

watch(() => props.document, updateDocument, { deep: true })
watch(() => props.activatable, () => {
  for (const { graphic, unit } of unitGraphics.values()) paintUnit(graphic, unit)
})
watch(() => props.focusedLocalityId, () => { pinnedUnitId = null; emit('inspectUnit', null); emit('hoverPosition', null); fit() })
watch(() => props.inspectedUnitId, inspectedUnitId => updateUnitVisualState({ inspectedUnitId }))
watch(() => props.ownedUnitIds, ownedUnitIds => updateUnitVisualState({ ownedUnitIds: ownedUnitIds ?? [] }), { deep: true })
watch(() => props.pendingUnitIds, pendingUnitIds => updateUnitVisualState({ pendingUnitIds: pendingUnitIds ?? [] }), { deep: true })
watch(
  [() => props.ownedLocalityIds, () => props.pendingLocalityIds],
  ([owned, pending]) => {
    const nextOwned = new Set(owned)
    const nextPending = new Set(pending)
    if (
      nextOwned.size === ownedLocalitySnapshot.size
      && nextPending.size === pendingLocalitySnapshot.size
      && (owned?.every(id => ownedLocalitySnapshot.has(id)) ?? true)
      && (pending?.every(id => pendingLocalitySnapshot.has(id)) ?? true)
    ) return
    ownedLocalitySnapshot = nextOwned
    pendingLocalitySnapshot = nextPending
    drawStaticContext()
    requestRender()
  },
  { deep: true }
)
watch(() => props.selectable, () => {
  pinnedUnitId = null
  updateUnitVisualState({ inspectedUnitId: null, hoveredUnitId: null })
  emit('inspectUnit', null)
  emit('hoverPosition', null)
  redraw()
})
watch(() => props.viewportCommand.id, handleViewportCommand)

onMounted(async () => {
  document.fonts.addEventListener('loadingdone', refreshMapFont)
  refreshMapFont()
  await nextTick()
  await mountPixi()
  if (disposed || !host.value) return
  resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !app || entry.contentRect.width <= 0 || entry.contentRect.height <= 0) return
    app.renderer.resize(
      entry.contentRect.width,
      entry.contentRect.height,
      preferredCanvasRendererResolution(window.devicePixelRatio)
    )
    app.stage.hitArea = new Rectangle(0, 0, entry.contentRect.width, entry.contentRect.height)
    fit()
  })
  resizeObserver.observe(host.value)
})

onBeforeUnmount(() => {
  disposed = true
  if (renderFrame !== null) cancelAnimationFrame(renderFrame)
  renderFrame = null
  document.fonts.removeEventListener('loadingdone', refreshMapFont)
  resizeObserver?.disconnect()
  unbindViewportGestures()
  clear(staticLayer)
  clear(interactionLayer)
  unitGraphics.clear()
  app?.destroy({ removeView: true }, { children: true })
  app = null
})
</script>

<template>
  <div ref="host" class="event-map-renderer-client" aria-hidden="true">
    <div class="event-map-renderer-client__markers">
      <EventMapIndividualSaleIcon
        v-for="marker in individualSaleMarkers"
        :key="marker.id"
        :expanded="marker.expanded"
        class="event-map-renderer-client__marker"
        :style="{ left: `${marker.x}px`, top: `${marker.y}px` }"
      />
    </div>
  </div>
</template>

<style scoped>
.event-map-renderer-client__markers {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none !important;
}
/* Decorative icons must stay transparent to clicks even inside a guided tour. */
.event-map-renderer-client__markers :deep(*) {
  pointer-events: none !important;
}
.event-map-renderer-client__marker.individual-sale-icon {
  width: 2rem;
  height: 2rem;
  position: absolute;
  transform: translate(-50%, -50%);
}
.event-map-renderer-client {
  position: relative;
  width: 100%;
  min-height: 37rem;
  cursor: grab;
  touch-action: none;
}

.event-map-renderer-client:active {
  cursor: grabbing;
}

@media (max-width: 640px) {
  .event-map-renderer-client {
    min-height: 24rem;
  }
}
</style>
